import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { name: 'Home', path: '/', type: 'link' },
    { name: 'Services', path: '/', sectionId: 'services', type: 'scroll' },
    { name: 'About', path: '/about', type: 'link' },
    { name: 'Portfolio', path: '/portfolio', type: 'link' },
    { name: 'Blog', path: 'https://medium.com/@thepratishjain', type: 'external' },
    { name: 'Contact', path: '/', sectionId: 'contact', type: 'scroll' }
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-sm shadow-md'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link
            to="/"
            onClick={() => window.scrollTo(0, 0)}
            className="text-2xl font-bold text-gray-800 hover:text-amber-600 transition-colors"
          >
            Pratish Jain
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              link.type === 'link' ? (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-gray-700 hover:text-amber-600 transition-colors font-medium ${
                    location.pathname === link.path ? 'text-amber-600' : ''
                  }`}
                >
                  {link.name}
                </Link>
              ) : link.type === 'external' ? (
                <a
                  key={link.name}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:text-amber-600 transition-colors font-medium"
                >
                  {link.name}
                </a>
              ) : (
                <button
                  key={link.name}
                  onClick={() => {
                    if (location.pathname !== '/') {
                      window.location.href = '/#' + link.sectionId;
                    } else {
                      scrollToSection(link.sectionId);
                    }
                  }}
                  className="text-gray-700 hover:text-amber-600 transition-colors font-medium"
                >
                  {link.name}
                </button>
              )
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-gray-700 hover:text-amber-600 transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 py-4">
            {navLinks.map((link) => (
              link.type === 'link' ? (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => handleNavigation(link.path)}
                  className={`block px-4 py-3 text-gray-700 hover:text-amber-600 hover:bg-amber-50 transition-colors ${
                    location.pathname === link.path ? 'text-amber-600 bg-amber-50' : ''
                  }`}
                >
                  {link.name}
                </Link>
              ) : link.type === 'external' ? (
                <a
                  key={link.name}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-3 text-gray-700 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                >
                  {link.name}
                </a>
              ) : (
                <button
                  key={link.name}
                  onClick={() => {
                    if (location.pathname !== '/') {
                      window.location.href = '/#' + link.sectionId;
                    } else {
                      scrollToSection(link.sectionId);
                    }
                  }}
                  className="block w-full text-left px-4 py-3 text-gray-700 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                >
                  {link.name}
                </button>
              )
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;